import React, { useEffect } from 'react';

const SOURCE_LABELS = {
  nfc: 'NFC tap',
  manual: 'Manual',
  invitation: 'Invitation',
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('') || '?';

/**
 * Read-only detail drawer for the Enterprise Contacts CRM (v1).
 * Shows the live card profile of a saved contact plus relationship metadata.
 * Personal notes are never received from the API, so never rendered here.
 */
export default function EnterpriseContactDrawer({ contact, onClose }) {
  useEffect(() => {
    if (!contact) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [contact, onClose]);

  if (!contact) return null;

  const fields = [
    { label: 'Email', value: contact.email, href: contact.email ? `mailto:${contact.email}` : null },
    { label: 'Phone', value: contact.phone, href: contact.phone ? `tel:${contact.phone}` : null },
    { label: 'Company', value: contact.company },
    { label: 'Role', value: contact.role },
    { label: 'Title', value: contact.title },
    { label: 'LinkedIn', value: contact.linkedinURL, href: contact.linkedinURL, external: true },
    { label: 'Website', value: contact.additionalURL, href: contact.additionalURL, external: true },
  ];

  return (
    <>
      <button
        type="button"
        className="ent-drawer-overlay"
        aria-label="Close contact details"
        onClick={onClose}
      />
      <aside className="ent-drawer" role="dialog" aria-modal="true" aria-label="Contact details">
        <div className="ent-drawer-head">
          <div className="ent-drawer-identity">
            <span className="ent-drawer-avatar" aria-hidden="true">{initials(contact.name)}</span>
            <div>
              <h2 className="ent-drawer-title">{contact.name || 'Unknown contact'}</h2>
              <p className="ent-drawer-subtitle">
                {[contact.role, contact.company].filter(Boolean).join(' · ') || 'No role / company on card'}
              </p>
            </div>
          </div>
          <button type="button" className="ent-drawer-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="ent-drawer-body">
          <section className="ent-drawer-section">
            <h3 className="ent-drawer-section-title">Card details</h3>
            {!contact.isLiveCard && (
              <p className="ent-drawer-stale-note">
                This Velox account no longer exists — showing the last saved card details.
              </p>
            )}
            <dl className="ent-drawer-fields">
              {fields.map(({ label, value, href, external }) => (
                <div className="ent-drawer-field" key={label}>
                  <dt>{label}</dt>
                  <dd>
                    {value ? (
                      href ? (
                        <a
                          href={href}
                          className="ent-drawer-link"
                          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          {value}
                        </a>
                      ) : (
                        value
                      )
                    ) : (
                      <span className="ent-drawer-empty">—</span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="ent-drawer-section">
            <h3 className="ent-drawer-section-title">Relationship</h3>
            <dl className="ent-drawer-fields">
              <div className="ent-drawer-field">
                <dt>First added</dt>
                <dd>{formatDate(contact.dateAddedFirst)}</dd>
              </div>
              <div className="ent-drawer-field">
                <dt>Latest added</dt>
                <dd>{formatDate(contact.dateAddedLatest)}</dd>
              </div>
              <div className="ent-drawer-field">
                <dt>Source</dt>
                <dd>
                  {(contact.sources || []).length > 0
                    ? contact.sources.map((s) => SOURCE_LABELS[s] || s).join(', ')
                    : '—'}
                </dd>
              </div>
              <div className="ent-drawer-field">
                <dt>Location</dt>
                <dd>
                  {contact.hasLocation ? (
                    <span className="ent-loc ent-loc-yes">
                      <span className="ent-loc-dot" aria-hidden="true" /> Location available
                    </span>
                  ) : (
                    <span className="ent-loc ent-loc-no">No location</span>
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section className="ent-drawer-section">
            <h3 className="ent-drawer-section-title">
              Saved by {contact.savedByCount} team member{contact.savedByCount === 1 ? '' : 's'}
            </h3>
            <ul className="ent-drawer-savers">
              {(contact.savedBy || []).map((s) => (
                <li className="ent-drawer-saver" key={`${s.userId}-${s.dateAdded}`}>
                  <span className="ent-drawer-saver-avatar" aria-hidden="true">{initials(s.name)}</span>
                  <span className="ent-drawer-saver-main">
                    <span className="ent-drawer-saver-name">{s.name}</span>
                    <span className="ent-drawer-saver-meta">
                      {[s.email, s.teamName].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                  <span className="ent-drawer-saver-date">{formatDate(s.dateAdded)}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </aside>
    </>
  );
}
